import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDistanceToNow } from "date-fns";
import CCPARequestCard from "@/components/CCPARequestCard";
import { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/components/ui/use-toast";
import { Header } from "@/components/Header";

type CCPARequest = Tables<"ccpa_requests">;

const CCPA = () => {
  const navigate = useNavigate();
  const [selectedRequest, setSelectedRequest] = useState<CCPARequest | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Set up real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('ccpa-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ccpa_requests'
        },
        (payload) => {
          queryClient.setQueryData(['ccpa_requests'], (oldData: CCPARequest[] | undefined) => {
            const newRequest = payload.new as CCPARequest;
            if (!oldData) return [newRequest];
            return [newRequest, ...oldData];
          });
          
          toast({
            title: "New CCPA Request Received",
            description: `${payload.new.name} has submitted a CCPA request.`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast]);

  const { data: requests, isLoading, error } = useQuery({
    queryKey: ['ccpa_requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ccpa_requests')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  if (error) {
    console.error("Error in CCPA requests query:", error);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">CCPA Requests Dashboard</h1>

          {isLoading ? (
            <div className="text-center py-8">Loading requests...</div>
          ) : !requests?.length ? (
            <div className="text-center py-8 text-gray-600">No CCPA requests found</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Request Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Time Since</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow 
                      key={request.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <TableCell className="font-medium">{request.name}</TableCell>
                      <TableCell>{request.email}</TableCell>
                      <TableCell className="capitalize">{request.request_type.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="capitalize">{request.status}</TableCell>
                      <TableCell>{new Date(request.created_at).toLocaleString()}</TableCell>
                      <TableCell>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedRequest && (
            <CCPARequestCard
              request={selectedRequest}
              isOpen={!!selectedRequest}
              onClose={() => setSelectedRequest(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CCPA;